import React, { FC, useEffect, useState } from 'react';

import { IsoCountryCode2Char } from '@rbilabs/common';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import { FormStatus } from 'enums/support-form';
import { useRequestRefundMutation } from 'generated/rbi-graphql';
import { useOrderDetails } from 'hooks/use-order-details';
import { useSupportData } from 'hooks/use-support-data';
import AuthRequired from 'pages/authentication/auth-required';
import {
  FAILED_REFUND,
  SUCCESSFUL_REFUND,
} from 'pages/report-order-never-arrived/report-order-never-arrived';
import { useLocale } from 'state/intl';
import { CartEntryType } from 'utils/cart';
import { StatusType, dataDogLogger } from 'utils/datadog';
import { brand } from 'utils/environment';
import { routes } from 'utils/routing';

import ReportOrderItemsMissingPageView from './report-order-items-missing.view';
import { IReportOrderItemsItem, IReportOrderItemsMissingPageViewProps } from './types';

export const NCR_MARKER_PLUS = {
  BK: {
    [IsoCountryCode2Char.GB]: '9088',
    DEFAULT: '7592',
  },
  LTW: {
    DEFAULT: '2549',
  },
  PLK: {
    DEFAULT: '2549',
  },
  TH: {
    DEFAULT: '0000',
  },
  FHS: {
    DEFAULT: '0000',
  },
};

/**
 * NCR requires markers to process combos. We insert NCR placeholders during order cart building.
 * These combo placeholders have a hidden UIPattern in sanity and are not shown to guests.
 * They have names such as "Combo Sides" and "Marker Item".
 *
 * They should also be hidden in the ReportOrderItemsMissingPage. So guests
 * cannot choose to refund placeholder items, since they do not represent real items.
 */
const isComboPlaceHolder = (item: IBackendCartEntries, region: string) => {
  const itemConstantPlu = item.vendorConfigs?.ncr ? item.vendorConfigs?.ncr['constantPlu'] : '';
  const rbiBrand = brand().toUpperCase();
  const ncrMarker = NCR_MARKER_PLUS[rbiBrand][region] ?? NCR_MARKER_PLUS[rbiBrand].DEFAULT;

  return itemConstantPlu === ncrMarker;
};

const isComboOrOffer = (item: IBackendCartEntries) =>
  item.type === CartEntryType.combo || item.type === CartEntryType.offerCombo;

const flattenChildren = (list: IBackendCartEntries[], quantity = 1) =>
  list.reduce<IBackendCartEntries[]>((prev, current): IBackendCartEntries[] => {
    if (current.children && current.type === CartEntryType.comboSlot) {
      // comboSlots hold items as children -- don't include current, but include its children instead
      return [...prev, ...flattenChildren(current.children, quantity)];
    }

    if (current.children && isComboOrOffer(current)) {
      return [...prev, current, ...flattenChildren(current.children, current.quantity)];
    }

    return [...prev, { ...current, quantity: quantity * current.quantity, children: undefined }];
  }, []);

/**
 * ReportOrderItemsMissingPage
 *
 * Used to report that an order was never received
 */
const ReportOrderItemsMissingPage: FC = () => {
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams({ categoryId: '' });
  const categoryId = searchParams.get('categoryId') ?? '';
  const { order, loading: orderLoading } = useOrderDetails(orderId);

  const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.UNSENT);
  const [processingRefundRequest, setProcessingRefundRequest] = useState(false);

  const [requestRefundMutation] = useRequestRefundMutation();
  const { deliveryMissingItemsSubcategory } = useSupportData();
  const issueId = deliveryMissingItemsSubcategory?.knowledgeForceIssueId || 'No Issue Id';

  const items: IReportOrderItemsItem[] =
    order?.cart?.cartEntries?.map(entry => {
      // Flatten top level item's children
      const children =
        (entry.children &&
          isComboOrOffer(entry) &&
          flattenChildren(entry.children, entry.quantity)) ||
        undefined;

      // Filter out loyalty entries and ncr placeholders
      const filteredChildren = children?.filter(
        child => child.lineId !== 'loyalty' && !isComboPlaceHolder(child, region)
      );

      const reducedChildren = filteredChildren?.map(child => ({
        id: child.lineId,
        quantity: child.quantity,
        name: child.name || 'missing item name',
      }));

      return {
        id: entry.lineId,
        quantity: entry.quantity,
        name: entry.name || 'missing item name',
        children: reducedChildren,
      };
    }) || [];

  const errorLinkQuery = new URLSearchParams({
    ...(categoryId && { categoryId }),
    ...(deliveryMissingItemsSubcategory?._id && {
      subcategoryId: deliveryMissingItemsSubcategory?._id,
    }),
    orderId,
  }).toString();

  const errorLink = `${routes.supportForm}?${errorLinkQuery}`;

  const language: IReportOrderItemsMissingPageViewProps = {
    headingText: formatMessage({ id: 'orderMissingItemsheading' }),
    subheadingText: formatMessage({ id: 'orderMissingItemssubheading' }),
    contentText: formatMessage({ id: 'orderMissingItemscontent' }),
    commentsPlaceholderText: formatMessage({ id: 'comments' }),
    submitButtonText: formatMessage({ id: 'itemsNeverArrived' }),
    refundSuccessText: formatMessage({ id: 'orderHasBeenRefundedMessage' }),
    refundErrorText: formatMessage(
      { id: 'orderCannotBeRefundedMessage' },
      {
        link: (
          <Link to={errorLink} data-testid="refund-error-link">
            {formatMessage({ id: 'refundClickHere' })}
          </Link>
        ),
      }
    ),
  };

  useEffect(() => {
    // TODO: Implement redirects here when no order is found
    if (!orderLoading && !order) {
      dataDogLogger({
        message: 'Order Never Arrived Customer Initiated Refund Network Error: Order not found',
        status: StatusType.error,
      });
    }
  }, [orderLoading, order]);

  const handleFormStatus = (status: string) => {
    // updates form status state
    setFormStatus(FormStatus[status]);
  };

  const onSubmit: IReportOrderItemsMissingPageViewProps['onSubmit'] = async fields => {
    setProcessingRefundRequest(true);
    const missingItems = fields.items.map(item => {
      if (!item.children) {
        return {
          lineId: item.id,
          refundQuantity: item.refundCount,
        };
      }

      /**
       * The backend is expecting a fraction of a combo or offer
       * in order to determine refund price.
       * Items in a combo or offer are counted and provided to the BE.
       *
       * The BE will determine the price based on the refundCount/TotalQuantity
       * fraction.
       */
      let refundQuantity = 0;
      const totalQuantity = item.children.length * item.quantity;

      // Calculate refund quantities on all items out of max.
      // If a root item is selected, consider all items in it refunded.
      if (item.refundCount > 0) {
        refundQuantity = item.children.length * item.refundCount;
      }
      // Add missing children to refundCount
      item.children.forEach(child => {
        refundQuantity += child.refundCount;
      });
      // if refundCount is higher than max, set to max.
      // This may happen if root items and children are both selected.
      if (refundQuantity > totalQuantity) {
        refundQuantity = totalQuantity;
      }

      return {
        lineId: item.id,
        refundQuantity,
        totalQuantity,
      };
    });

    try {
      const response = await requestRefundMutation({
        variables: {
          input: {
            rbiOrderId: order?.rbiOrderId || '',
            issueId,
            userComment: fields.comments,
            missingItems,
          },
        },
      });
      if (response.data?.userRequestsRefund) {
        handleFormStatus(SUCCESSFUL_REFUND);
      } else {
        handleFormStatus(FAILED_REFUND);
      }
      setProcessingRefundRequest(false);
    } catch (e) {
      dataDogLogger({
        message: `Missing Items Customer Initiated Refund Network Error: ${e}`,
        status: StatusType.error,
      });
      setProcessingRefundRequest(false);
      handleFormStatus(FAILED_REFUND);
    }
  };

  if (orderLoading) {
    return (
      <LoadingContainer>
        <LoadingAnimation fillColor={Styles.color.primary} />
      </LoadingContainer>
    );
  }

  return (
    <AuthRequired>
      <Helmet title={formatMessage({ id: 'orderMissingItemsheading' })} />
      <ReportOrderItemsMissingPageView
        isLoading={processingRefundRequest}
        items={items}
        onSubmit={onSubmit}
        disableSubmitButton={!order || formStatus !== FormStatus.UNSENT}
        onBack={() => window.history.back()}
        {...language}
        formStatus={formStatus}
      />
    </AuthRequired>
  );
};

export default ReportOrderItemsMissingPage;
